import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../context/AuthContext';
import Wordmark from '../components/Wordmark';

const SetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setPassword: setPasswordApi } = useAuth();

  const email = location.state?.email || '';
  const resultId = location.state?.result_id || '';
  const name = location.state?.name || '';

  const [password, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!email || !resultId) {
      navigate('/');
    }
  }, [email, resultId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await setPasswordApi(email, password, confirm, resultId);
      navigate('/preview', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Could not create account. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <Wordmark />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          <h1
            className="text-3xl sm:text-4xl font-normal mb-3 leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            {name ? `Nice work, ${name}.` : 'Nice work.'}
          </h1>
          <p className="text-sm sm:text-base text-[#1a1a1a]/70 mb-6 sm:mb-8">
            Set a password to save your compliance package and access your dashboard anytime.
          </p>

          {error && (
            <div
              data-testid="set-password-error-message"
              className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6"
            >
              <p>{error}</p>
              {/^account already exists/i.test(error) && (
                <p className="mt-2">
                  <Link
                    to="/login"
                    data-testid="set-password-signin-link"
                    className="font-medium text-[#0a5c44] underline hover:no-underline"
                  >
                    Already have an account? Sign in here →
                  </Link>
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-base font-normal text-[#1a1a1a]">Email</Label>
              <Input
                data-testid="set-password-email-readonly"
                value={email}
                readOnly
                className="bg-[#f5f3ef] border-[#e0ddd8] mt-2 cursor-not-allowed"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-base font-normal text-[#1a1a1a]">
                Create password
              </Label>
              <PasswordInput
                id="password"
                data-testid="set-password-password-input"
                testIdToggle="set-password-password-toggle"
                value={password}
                onChange={(e) => setPwd(e.target.value)}
                className="bg-white border-[#e0ddd8] mt-2"
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </div>

            <div>
              <Label htmlFor="confirm" className="text-base font-normal text-[#1a1a1a]">
                Confirm password
              </Label>
              <PasswordInput
                id="confirm"
                data-testid="set-password-confirm-input"
                testIdToggle="set-password-confirm-toggle"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-white border-[#e0ddd8] mt-2"
                placeholder="Re-enter password"
                required
                minLength={8}
              />
            </div>

            <Button
              type="submit"
              data-testid="set-password-submit-button"
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors h-11"
            >
              {loading ? 'Creating account...' : 'Create account & continue'}
            </Button>
          </form>

          <p className="text-xs text-[#1a1a1a]/60 mt-6 text-center">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="underline">Terms</Link> and{' '}
            <Link to="/privacy" className="underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
